import clsx from 'clsx'
import { Button, Spinner } from 'gu-ui-kit'
import omit from 'lodash/omit'
import formStyles from 'modules/Forms/common-form.module.scss'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import {
  formValueSelector,
  reduxForm,
  setSubmitFailed,
  stopAsyncValidation,
} from 'redux-form'
import extractErrors from 'utils/extractErrors'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import styles from 'modules/Forms/Account/Create/account-form-creation.module.scss'
import AdvertiserFieldsExtended from 'modules/Forms/Account/Create/subcomponents/advertiserFieldsExtended'
import AdvertisersTemplatesField from '../../Account/Create/subcomponents/AdvertisersTemplatesField'
import { notificationShow } from 'modules/Notifications/actions'
import { PATCH_ACCOUNT_ADVERTISER_INFO } from 'modules/Services/types'

let AdvertiserInfoEditForm = ({
  handleSubmit,
  submitting,
  onClose = () => {},
  form,
  change,
  networkType = '',
  networkId = null,
  accountLogin = null,
  accountId,
  advertiser_info_errors = [],
}) => {
  const { t } = useTranslation()
  const selector = formValueSelector(form)
  const dispatch = useDispatch()

  const { advertiser_info, advertiser_template, advertisers } = useSelector(
    (state) => ({
      advertiser_info: selector(state, 'advertiser_info'),
      advertiser_template: selector(state, 'advertiser_template'),
      advertisers: state['advertisers-list'].data,
    }),
  )

  const submit = useCallback(
    (values, dispatch) =>
      new Promise((resolve, reject) => {
        dispatch({
          meta: { resolve, reject },
          type: PATCH_ACCOUNT_ADVERTISER_INFO,
          payload: {
            accountId: accountId,
            data: omit(values.advertiser_info, ['status', 'id']),
            formName: form,
          },
        })
      }).then(onClose),
    [],
  )

  useEffect(() => {
    change(
      'advertiser_info',
      advertisers.find(({ id }) => id === advertiser_template),
    )
  }, [advertiser_template])

  useEffect(() => {
    if (advertiser_info_errors.length === 0) {
      return
    }

    const errorFields = extractErrors(advertiser_info_errors)

    if (errorFields?.form) {
      dispatch(
        notificationShow({
          type: 'error',
          message: errorFields.form,
          delay: 10000,
        }),
      )
    }

    if (errorFields?.fields && Object.keys(errorFields.fields).length > 0) {
      dispatch(stopAsyncValidation(form, errorFields.fields))
      dispatch(
        setSubmitFailed(
          form,
          ...advertiser_info_errors.map((err) => Object.keys(err)[0]),
        ),
      )
    }
  }, [advertiser_info_errors])

  return (
    <>
      <form onSubmit={handleSubmit(submit)}>
        <div className={'margin-bottom-16'}>
          <AdvertisersTemplatesField
            change={change}
            headerText={
              <>
                {t('services::forms.shared_fields.advertiser.info.title', {
                  context: 'account',
                  login: accountLogin,
                })}{' '}
                {!accountLogin && (
                  <Spinner width={16} color={'Label-GU-Primary'} />
                )}
              </>
            }
            networkId={networkId}
          />
        </div>

        <AdvertiserFieldsExtended
          advertiser_info={advertiser_info}
          networkType={networkType}
        />

        <div
          className={clsx(
            formStyles['formControl__action-buttons'],
            styles['action-buttons'],
          )}
        >
          <Button
            disabled={submitting}
            onClick={onClose}
            variant={'ghost'}
            grey
            size="l"
            type="button"
          >
            {t('common::form_buttons.cancel')}
          </Button>
          <Button
            disabled={submitting}
            size="l"
            variant="primary"
            type="submit"
            bgColor={'Label-GU-Primary'}
          >
            {t('services::forms.advertiser_info.save')}
          </Button>
        </div>
      </form>
    </>
  )
}

AdvertiserInfoEditForm = reduxForm({
  form: 'AdvertiserInfoEditForm',
  enableReinitialize: true,
  shouldAsyncValidate: shouldAsyncValidate,
  touchOnChange: true,
})(AdvertiserInfoEditForm)

AdvertiserInfoEditForm.propTypes = {
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  blur: PropTypes.func,
  focus: PropTypes.func,
  change: PropTypes.func,
  valid: PropTypes.bool,
  onClose: PropTypes.func,
  touch: PropTypes.func,
  form: PropTypes.string,
  accountLogin: PropTypes.string,
  accountId: PropTypes.string,
  i18n: PropTypes.string,
  pristine: PropTypes.bool,
  networkType: PropTypes.string,
  networkId: PropTypes.string,
  advertiser_info_errors: PropTypes.array,
}

export default AdvertiserInfoEditForm
